import { useEffect, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import moment from 'moment';
import SipHttp from '../services/sip.service';
import { userStore } from 'store/UserStore';
import { Pagination } from 'shared/common/pagination';
import Loader from 'shared/common/loader';
import dashboardService from 'services/dashboard.service';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import ConfirmActionModal from './ConfirmActionModal';
import '../styles/SipPassbook.css';

const METAL_MAP: Record<string, string> = {
  Au: 'Gold',
  Ag: 'Silver',
  Pt: 'Platinum',
  Pd: 'Palladium',
};

const PAGE_SIZE = 10;

const STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  ACTIVE:   { bg: '#E6F4EC', color: '#1A6637' },
  INACTIVE: { bg: '#F1F2F4', color: '#5C6470' },
  PAUSED:   { bg: '#FEF6E0', color: '#7A5010' },
  EXPIRED:  { bg: '#F1F2F4', color: '#5C6470' },
};
const getStatusStyle = (status: string) =>
  STATUS_STYLE[status] ?? { bg: '#F1F2F4', color: '#5C6470' };

const FREQ_LABEL: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

const SipPassbook = () => {
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAutoRenewModal, setShowAutoRenewModal] = useState(false);
  const [autoRenewTarget, setAutoRenewTarget] = useState<any>(null);

  const [tempFilter, setTempFilter] = useState({
    frequency: '',
    metal: '',
    start_date: '',
  });

  const [user, customerId] = userStore((state: any) => [
    state.user,
    state.customerId,
  ]);

  const [metaData, setMetaData] = useState<any>({ current: 0, pages: 0 });

  const [filter, setFilter] = useState({
    customerId,
    metal: '',
    frequency: '',
    start_date: '',
  });

  const confirmToggleAutoRenew = async () => {
    if (!autoRenewTarget) return;
    setList(prev =>
      prev.map(sip =>
        sip.id === autoRenewTarget.id ? { ...sip, autoRenewStatus: 'DISABLED' } : sip
      )
    );
    try {
      await SipHttp.changeSIPAutoRenewalStatus(autoRenewTarget.id);
      toast.success('Auto-renew disabled permanently');
    } catch {
      setList(prev =>
        prev.map(sip =>
          sip.id === autoRenewTarget.id ? { ...sip, autoRenewStatus: 'ENABLED' } : sip
        )
      );
      toast.error('Failed to disable auto-renew');
    }
    setShowAutoRenewModal(false);
    setAutoRenewTarget(null);
  };

  const toggleAccordion = (id: string) =>
    setExpanded(expanded === id ? null : id);

  const getSIPList = async (pageNumber = 0) => {
    try {
      setLoading(true);
      const resp = await SipHttp.getSIPList(
        { ...filter, page: pageNumber, pageSize: PAGE_SIZE },
        user?.userId
      );
      if (resp.status === 200) {
        setList(resp?.data?.data || []);
        setMetaData({
          current: resp?.data?.meta?.page,
          pages: resp?.data?.meta?.totalPages + 1,
        });
      }
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  // const handleViewClick = async (sip: any) => {
  //   console.log(sip,"@sip");
  //   try {
  //     const res: any = await dashboardService.getAllMetals();
  //     console.log(res,"@res");
  //     if (res.status === 200 && res.data.data) {
  //       const metalMatch = res.data.data.result?.find((m: any) => sip?.metal === m?.metalCode);
  //       if (metalMatch) {
  //         sip['itemCode'] = metalMatch?.itemCode;
  //         history.push(`/sip-payment-schedule/${sip.id}?data=${encodeURIComponent(JSON.stringify(sip))}`);
  //       } else {
  //         toast.error('Something went wrong');
  //       }
  //     }
  //   } catch (e: any) {
  //     console.log(e.message);
  //   }
  // };

  const handleViewClick = async (sip: any) => {
  try {
    const res: any = await dashboardService.getAllMetals();
    if (res.status === 200 && res.data.data) {
      const results = res.data.data.result;

      if (sip.isMultiMetal) {
        // Map itemCode for each metal in the metals array
        const updatedMetals = sip.metals.map((m: any) => {
          const match = results.find((r: any) => r.metalCode === m.metal);
          return {
            ...m,
            itemCode: match?.itemCode ?? null,
          };
        });

        const anyMissing = updatedMetals.some((m: any) => !m.itemCode);
        if (anyMissing) {
          toast.error('Something went wrong');
          return;
        }

        sip['metals'] = updatedMetals;
      } else {
        // Single metal — existing logic
        const metalMatch = results.find((r: any) => sip?.metal === r?.metalCode);
        if (!metalMatch) {
          toast.error('Something went wrong');
          return;
        }
        sip['itemCode'] = metalMatch?.itemCode;
      }

      history.push(`/sip-payment-schedule/${sip.id}?data=${encodeURIComponent(JSON.stringify(sip))}`);
    }
  } catch (e: any) {
    console.log(e.message);
  }
};

  useEffect(() => { getSIPList(0); }, [filter]);

  return (
    <div className="sip-passbook-wrapper">

      {/* ── Filter card ─────────────────────────────────────────────────── */}
      <div className="sip-filter-card">
        <div className="sip-filter-row">

          <div className="sip-filter-group">
            <label className="sip-label">Investment Frequency</label>
            <select
              className="sip-select"
              value={tempFilter.frequency || 'All'}
              onChange={(e) =>
                setTempFilter(p => ({ ...p, frequency: e.target.value === 'All' ? '' : e.target.value }))
              }
            >
              <option value="All">All</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>

          <div className="sip-filter-group">
            <label className="sip-label">Metal Type</label>
            <select
              className="sip-select"
              value={tempFilter.metal || 'All'}
              onChange={(e) =>
                setTempFilter(p => ({ ...p, metal: e.target.value === 'All' ? '' : e.target.value }))
              }
            >
              <option value="All">All</option>
              <option value="Au">Gold</option>
              <option value="Ag">Silver</option>
              <option value="Pt">Platinum</option>
              <option value="Pd">Palladium</option>
            </select>
          </div>

          <div className="sip-filter-group">
            <label className="sip-label">Start Date</label>
            <input
              className="sip-input"
              type="date"
              value={tempFilter.start_date}
              onChange={(e) => setTempFilter(p => ({ ...p, start_date: e.target.value }))}
            />
          </div>

          <div className="sip-filter-group">
          </div>

          <div className="sip-filter-actions">
            <button
              className="sip-btn-primary"
              onClick={() => setFilter({ ...tempFilter, customerId })}
            >
              Apply
            </button>
            <button
              className="sip-btn-secondary"
              onClick={() => {
                setTempFilter({ frequency: '', metal: '', start_date: '' });
                setFilter({ customerId, frequency: '', metal: '', start_date: '' });
              }}
            >
              Reset
            </button>
          </div>

        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <div className="sip-table-wrapper">
        <div className="sip-table-scroll">
          <table className="sip-table">
            <colgroup>
              <col className="sip-col-ref" />
              <col className="sip-col-plan" />
              <col className="sip-col-type" />
              <col className="sip-col-freq" />
              <col className="sip-col-date" />
              <col className="sip-col-tenure" />
              <col className="sip-col-amount" />
              <col className="sip-col-status" />
              <col className="sip-col-renew" />
              <col className="sip-col-action" />
            </colgroup>

            <thead>
              <tr>
                <th>SIP Ref</th>
                <th>Plan Name</th>
                <th>Investment Type</th>
                <th>Frequency</th>
                <th>Start Date</th>
                <th>Tenure</th>
                <th className="right">Amount</th>
                <th>Status</th>
                <th className="center">Auto-renew</th>
                <th className="center">Schedule</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="sip-empty">
                    <Loader />
                  </td>
                </tr>
              ) : list.length === 0 ? (
                <tr>
                  <td colSpan={10} className="sip-empty">
                    <div className="sip-empty-icon">📭</div>
                    <div className="sip-empty-text">No SIP records found</div>
                  </td>
                </tr>
              ) : (
                list.map((sip: any) => {
                  const isExpanded = expanded === sip.id;
                  const sc = getStatusStyle(sip.status);
                  const investmentLabel = sip.isMultiMetal
                    ? 'Multi-Metal SIP'
                    : sip.isGoldPlusSilver
                    ? 'Gold + Silver SIP'
                    : `${METAL_MAP[sip.metal] ?? sip.metal} SIP`;

                  const metalBreakup = sip.isMultiMetal
                    ? sip.metals
                    : [{ metal: sip.metal, metalInstallmentAmount: sip.installmentAmt }];

                  const totalAmount = sip.isMultiMetal
                    ? sip.metals.reduce((s: number, m: any) => s + (m.metalInstallmentAmount || 0), 0)
                    : sip.installmentAmt;

                  return (
                    <tr
                      key={sip.id}
                      className={`sip-row${sip.status === 'INACTIVE' ? ' sip-row--inactive' : ''}`}
                    >
                      {/* SIP Ref */}
                      <td>
                        <span className="sip-ref-id">{sip.id}</span>
                      </td>

                      {/* Plan Name */}
                      <td>
                        <span className="sip-plan-name">{sip.planName}</span>
                      </td>

                      {/* Investment Type + breakdown */}
                      <td>
                        <div className="sip-type-cell">
                          <button
                            className={`sip-type-btn${isExpanded ? ' active' : ''}`}
                            onClick={() => toggleAccordion(sip.id)}
                          >
                            <span className="sip-type-label">{investmentLabel}</span>
                            <svg
                              className={`sip-chevron${isExpanded ? ' open' : ''}`}
                              width="11" height="11" viewBox="0 0 24 24" fill="none"
                            >
                              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.2"
                                strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>

                          <Collapse in={isExpanded}>
                            <div className="sip-breakdown">
                              {metalBreakup.map((m: any, idx: number) => (
                                <div key={idx} className="sip-breakdown-row">
                                  <span className="sip-breakdown-metal">
                                    {METAL_MAP[m.metal] || m.metal}
                                  </span>
                                  <span className="sip-breakdown-amt">
                                    ₹{(m.metalInstallmentAmount || 0).toLocaleString()}
                                  </span>
                                </div>
                              ))}
                              <div className="sip-breakdown-row sip-breakdown-total">
                                <span className="sip-breakdown-metal">Total</span>
                                <span className="sip-breakdown-amt">
                                  ₹{totalAmount.toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </Collapse>
                        </div>
                      </td>

                      {/* Frequency */}
                      <td>
                        <span className="sip-freq-badge">
                          {FREQ_LABEL[sip.frequency] ?? sip.frequency}
                        </span>
                      </td>

                      {/* Start Date */}
                      <td>
                        <div className="sip-date">{moment(sip.startDate).format('DD MMM YYYY')}</div>
                      </td>

                      {/* Tenure */}
                      <td>
                        <span className="sip-tenure">{sip.duration} months</span>
                      </td>

                      {/* Amount */}
                      <td className="right">
                        <span className="sip-amount">₹{sip.installmentAmt.toLocaleString()}</span>
                      </td>

                      {/* Status */}
                      <td>
                        <span
                          className="sip-status-badge"
                          style={{ background: sc.bg, color: sc.color }}
                        >
                          {sip.status}
                        </span>
                      </td>

                      {/* Auto-renew toggle */}
                      <td className="center">
                        <label className="sip-switch">
                          <input
                            type="checkbox"
                            checked={sip.autoRenewStatus === 'ENABLED' && sip.isAutoRenew}
                            disabled={
                              sip.autoRenewStatus !== 'ENABLED' ||
                              sip.status !== 'ACTIVE' ||
                              moment.utc(sip?.endDate).isSame(moment.utc(), 'day')
                            }
                            onChange={() => {
                              setAutoRenewTarget(sip);
                              setShowAutoRenewModal(true);
                            }}
                          />
                          <span className="sip-slider" />
                        </label>
                      </td>

                      {/* Schedule */}
                      <td className="center">
                        <button className="sip-view-btn" onClick={() => handleViewClick(sip)}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8 7V3M16 7V3M3 11H21M5 5H19C20.1 5 21 5.9 21 7V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V7C3 5.9 3.9 5 5 5Z"
                              stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round" strokeLinejoin="round"
                            />
                          </svg>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && metaData.pages > 1 && (
        <Pagination
          meta={metaData}
          handlePageChange={(page: number) => getSIPList(page)}
        />
      )}

      <ConfirmActionModal
        show={showAutoRenewModal}
        onCancel={() => { setShowAutoRenewModal(false); setAutoRenewTarget(null); }}
        onConfirm={confirmToggleAutoRenew}
        title="Disable Auto-renew?"
        description={
          <>
            Are you sure you want to disable auto-renew for{' '}
            <strong>{autoRenewTarget?.planName}</strong>?
            Once disabled, you will not be able to enable it again.
          </>
        }
        confirmText="Disable Auto-renew"
        intent="warning"
      />
    </div>
  );
};

export default SipPassbook;